/**
 * Editorial project stories (Architectural Digest-style), written to be found.
 *
 * Each story drives: the project page body (facts + sections), its <title>/description,
 * and the Projects/homepage card text. Projects without a story keep the generic layout.
 * Quotes are only added once the person has approved the exact words — never invented.
 *
 * Gubbi Gudu keyword targets: villa interior design JP Nagar / Anjanapura · G+4 independent
 * house interiors Bangalore · sunken living room · open pooja room · island kitchen without
 * loft · walk-in closet · kids bunk bed · contemporary classic interiors.
 */

export interface ProjectStory {
  slug: string;
  name: string;
  location: string;
  propertyType: string;
  year: string;
  tagline: string;
  metaTitle: string;
  metaDescription: string;
  /** Short line for project cards */
  cardDescription: string;
  facts: { label: string; value: string }[];
  /** photo: part of a photo's file name to show right after that section (matches the text) */
  sections: { heading: string; paragraphs?: string[]; bullets?: string[]; photo?: string }[];
  quotes?: { text: string; by: string }[];
  /** Links to the Ideas / service pages that explain the designs used in this home */
  related?: { label: string; href: string }[];
}

export const GUBBI_GUDU: ProjectStory = {
  slug: 'gubbi-gudu-villa-anjanapura',
  related: [
    { label: "Interior design for independent houses and villas", href: '/independent-house-interiors-bangalore' },
    { label: "TV wall ideas — panelled TV walls and statement consoles", href: '/ideas/tv-unit-designs' },
    { label: "Wardrobe designs with real costs", href: '/ideas/wardrobe-designs' },
  ],
  name: 'Gubbi Gudu',
  location: 'Anjanapura, JP Nagar, Bangalore',
  propertyType: 'Independent villa · G+4',
  year: 'Completed',
  tagline:
    'For a three-generation family in a 7,500 sq ft, G+4 villa off Bannerghatta Road, the designers set one goal: make every room feel larger than life.',
  metaTitle: "Gubbi Gudu: G+4 Villa Interiors, Anjanapura | Willow",
  metaDescription:
    "A 7,500 sq ft G+4 villa in Anjanapura, Bangalore: sunken living room, open pooja room, loft-free island kitchen and walk-in closet — built by Willow.",
  cardDescription:
    'A 7,500 sq ft G+4 villa with a sunken living room, open pooja room and island kitchen — contemporary classic in walnut and white.',
  facts: [
    { label: 'Location', value: 'Anjanapura, JP Nagar (near Bannerghatta Road), Bangalore' },
    { label: 'Type', value: 'Independent villa, G+4 · 7,500 sq ft' },
    { label: 'Interior design', value: 'Ms. Navyashree & Ms. Sandhya Jamadagni' },
    { label: 'Manufacturing & execution', value: 'Willow Interiors' },
    { label: 'Client', value: 'Mr. Nikhil Kulkarni' },
    { label: 'Photography', value: 'Agith & Ravi' },
  ],
  sections: [
    {
      heading: 'A home for three generations',
      photo: 'gubbi-gudu-g4-villa-exterior',
      paragraphs: [
        'Gubbi Gudu — Kannada for “sparrow’s nest” — is home to a couple, their young child and their parents. They wanted a house that felt cozy yet open, and premium without feeling heavy. The designers spent close to a year refining it through many iterations.',
      ],
    },
    {
      heading: 'Contemporary classic, designed to feel bigger',
      paragraphs: [
        'The design language is contemporary classic — modern lines with Indian touches. Despite tight spots, every decision makes the villa feel larger: tall white shutters, large windows, full-height curtains, seamless doors and no false ceiling, so the full ceiling height stays visible. The palette is walnut brown and white, with soft grey in the bedrooms to keep them warm and calm.',
      ],
    },
    {
      heading: 'Where tradition meets the living room',
      photo: 'open-pooja-room-with-carved',
      paragraphs: [
        'The open pooja room, with its pillars and turned details, sits right beside a strikingly contemporary sunken living room. Instead of competing, the two blend into each other — tied together by blue pebbles in graded shades, an ombré from traditional to modern.',
      ],
    },
    {
      heading: 'A TV wall that holds its own',
      photo: 'living-room-walnut-tv-unit-and-fluted-panel-wide',
      paragraphs: [
        'Squeezed between the living room and the internal courtyard, the TV wall had a short span and a lot of competition — the pooja room, the pebbles, the furniture. The answer is an angled unit, part carpentry and part classic, that is strong without being heavy. Beside it, long wooden rafters run floor to ceiling, drawing the eye upward — and hidden among them is a seamless concealed door to the powder room.',
      ],
    },
    {
      heading: 'An island kitchen without a loft',
      photo: 'island-kitchen-with-suspended-chimney',
      paragraphs: [
        'With generous ceiling heights, the designers skipped the kitchen loft entirely — a rare, confident choice that keeps the kitchen open. The centrepiece is the island, and its chimney duct cladding was designed to add to the look rather than weigh it down.',
      ],
    },
    {
      heading: 'Bedrooms with depth',
      photo: 'master-bedroom-with-moon-motif',
      paragraphs: [
        'The master bedroom, with its walk-in closet, centres on a hand-finished POP feature wall with a moon motif that adds depth and complements the jaali. The kids’ room is kept deliberately simple in brown and white around a well-planned bunk bed — a choice that will grow with a child under three. The parents’ bedroom uses a textured grey wall for warmth.',
      ],
    },
    {
      heading: 'How it was built',
      paragraphs: ['Every unit was factory-finished in Willow’s manufacturing unit in JP Nagar, Bangalore.'],
      bullets: [
        'Kitchen in 2 mm acrylic, pasted by automated machinery for a mirror-clean, seamless finish — with Rehau edge banding and Hettich and Häfele fittings.',
        'Chimney cladding built on a fabricated metal frame, clad in plywood and laminate, aligned perfectly on all three sides.',
        'A concealed powder-room door in plywood and laminate, built to read as part of the rafters.',
        'The moon wall hand-built in POP instead of ready-made panels — no joints — and painted in stages with shaded patches for a natural, stone-like finish.',
        'A floating bedside table from a single plywood piece, engineered to be structurally sound.',
        'Modules built to Willow’s Ennezz Interior Standards (EIS) for load-bearing strength and a longer life — and each one can be replaced individually, in any year.',
      ],
    },
  ],
  quotes: [], // add only after the designers / client approve the exact words
};

// Keyword targets: 2.5 BHK apartment interiors Electronic City / Hosa Road · modular interiors
// for long rectangular rooms · TV unit with hidden door · pooja unit in study · maximum storage
// small apartment · budget modular interiors Bangalore · beige pastel modular kitchen.
export const RASHA: ProjectStory = {
  slug: 'rasha-apartment-hosa-road-electronic-city',
  related: [
    { label: "Printed laminate wardrobes, like the floral one in the study", href: '/ideas/wardrobe-designs#printed-laminate-wardrobe' },
    { label: "TV unit designs with real costs", href: '/ideas/tv-unit-designs' },
  ],
  name: 'Rasha',
  location: 'SJR Parkway Homes, Hosa Road, Electronic City, Bangalore',
  propertyType: 'Apartment · 2.5 BHK · 1,600 sq ft',
  year: 'Completed',
  tagline:
    'A 1,600 sq ft apartment of long, narrow rooms, a couple who wanted maximum storage and real character — on a tight budget. Designed, manufactured and built by Willow in 50 days.',
  metaTitle: "Rasha: 2.5 BHK Interiors, SJR Parkway Homes | Willow",
  metaDescription:
    "A 1,600 sq ft 2.5 BHK at SJR Parkway Homes, Electronic City: smart storage, a TV unit hiding a study door and a beige kitchen — done in 50 days.",
  cardDescription:
    'A 2.5 BHK on Hosa Road where long, narrow rooms met maximum storage — curves, glass and metal keep it light. Done in 50 days.',
  facts: [
    { label: 'Location', value: 'SJR Parkway Homes, Hosa Road, Electronic City, Bangalore' },
    { label: 'Type', value: 'Apartment · 2.5 BHK · 1,600 sq ft' },
    { label: 'Spaces', value: 'Living, dining, foyer, kitchen, master bedroom, guest bedroom, study' },
    { label: 'Interior design', value: 'Meghana Prabhadeva, Willow Interiors' },
    { label: 'Design, manufacturing & execution', value: 'Willow Interiors' },
    { label: 'Clients', value: 'Mr. Rajath & Ms. Shalu' },
    { label: 'Timeline', value: '50 days' },
    { label: 'Photography', value: 'Agith & Ravi' },
  ],
  sections: [
    {
      heading: 'Long rooms, big wishes',
      photo: 'beige-wardrobe-built-around-window',
      paragraphs: [
        'Rasha is home to Rajath and Shalu, in a 2.5 BHK apartment at SJR Parkway Homes, Hosa Road, Electronic City. Almost every room — the living room, the master bedroom, the study — was a long, narrow rectangle. The couple wanted maximum storage, but they did not want plain cupboards: they loved texture, detail and darker tones, and they wanted the home to feel grand.',
        'All of it had to work on a tight budget, and stay modular — the hardest combination in interiors, because every custom detail adds cost.',
      ],
    },
    {
      heading: 'Light, texture and gentle curves',
      paragraphs: [
        'Dark shades would have made a low-ceilinged apartment feel smaller, so the designer chose a soft, textured pastel palette instead. Glass shutters lighten the larger units, and soft curves round off the heaviest pieces — on the study unit, the TV unit, the wall behind the sofa and even the hidden door.',
        'Because laminates were the right choice for the budget, the aesthetic spend went where it shows most: glass shutters and metal fabrication. The metalwork adds depth, breaks up large surfaces and quietly absorbs the heaviness of so much storage — you can see it under the kitchen counter. Fittings are by Ebco.',
      ],
    },
    {
      heading: 'A TV wall that changed direction',
      paragraphs: [
        'The original TV wall was too short. So the layout was turned around: the TV moved to the longer wall — which was broken by the entrance to the study. The solution was a hidden door, built into the TV unit itself, with a metal partition balancing the shorter side. The result is a larger TV, more display and storage, and a wall that looks seamless and premium.',
      ],
    },
    {
      heading: 'A pooja unit that flows into the study',
      photo: 'l-shaped-study-table-with-arched-pooja-niche',
      paragraphs: [
        'In the study, the pooja unit continues into the study table — one composition instead of two pieces of furniture competing for a narrow room.',
      ],
    },
    {
      heading: 'An elongated master bedroom, fully used',
      photo: 'beige-wardrobe-with-black-glass-unit',
      paragraphs: [
        'The master bedroom was unusually long and needed a lot: tall wardrobes, smaller units, storage boxes on different walls and a bed with a POP headboard. Varying the depths of the units on each wall kept the room from feeling like a corridor of cupboards — a layout that took real care to get right.',
      ],
    },
    {
      heading: 'A beige modular kitchen',
      photo: 'l-shaped-modular-kitchen-with-wooden-lofts',
      paragraphs: [
        'The kitchen follows the pastel palette in soft beige, with wooden lofts and lit glass shutters, and a band of metal fabrication beneath the counter that ties it to the rest of the home.',
      ],
    },
    {
      heading: 'How it was built',
      paragraphs: ['Every unit was manufactured in Willow’s unit in JP Nagar, Bangalore, and installed by our own team in 50 days.'],
      bullets: [
        'Textured laminates throughout — the right finish for the budget, chosen for character rather than plainness.',
        'Glass shutters and custom metal fabrication to add depth without adding visual weight.',
        'A hidden door to the study built into the TV unit, with a metal partition on the shorter side.',
        'Curved edges on the study unit, TV unit, sofa wall and hidden door.',
        'Ebco hardware; modules built to Willow’s Ennezz Interior Standards (EIS) for a longer life.',
      ],
    },
  ],
  quotes: [
    { text: 'Creativity always comes handy.', by: 'Meghana Prabhadeva, interior designer' },
    { text: 'Ample storage that looks amazing — wow, we didn’t know they could coexist.', by: 'Rajath & Shalu' },
  ],
};

// Keyword targets: G+2 independent house interiors Kanakapura Road · 20x40 house interiors ·
// budget interiors Bangalore · acrylic-look kitchen on a budget (PVC laminate on HDHMR) ·
// wall panelling without visible screws · loft vs floor-to-ceiling wardrobe cost ·
// seamless handle vs external handle cost · brown and beige interiors.
export const SARAL_MANE: ProjectStory = {
  slug: 'saral-mane-g2-house-kanakapura-road',
  related: [
    { label: "Interior design for independent houses", href: '/independent-house-interiors-bangalore' },
    { label: "Bed back panel ideas, like the walnut headboard wall", href: '/ideas/bed-back-wall-designs#bed-back-panel' },
    { label: "Grooved panel TV walls with a stone-look inlay", href: '/ideas/tv-unit-designs#classic-grooved-panel-tv-wall' },
  ],
  name: 'Saral Mane',
  location: 'Kanakapura Road, Bangalore',
  propertyType: 'Independent house · G+2 · 3,200 sq ft',
  year: 'Completed',
  tagline:
    'A G+2 home on a 20×40 plot, for a young couple and their parents who wanted interiors that are easy to live with — designed by Nord Studio, built by Willow on a tight budget.',
  metaTitle: "Saral Mane: G+2 House Interiors, Kanakapura Rd | Willow",
  metaDescription:
    "A 3,200 sq ft G+2 house on Kanakapura Road: brown and beige interiors, an acrylic-look kitchen on a budget and wall panels with no visible screws.",
  cardDescription:
    'A G+2 house on a 20×40 plot — brown, beige and soft grey, an acrylic-look kitchen on a budget, and lessons on what really drives interior cost.',
  facts: [
    { label: 'Location', value: 'Kanakapura Road, Bangalore' },
    { label: 'Type', value: 'Independent house, G+2 · 3,200 sq ft · 20×40 plot' },
    { label: 'Spaces', value: '3 BHK, living, pooja, study' },
    { label: 'Interior design', value: 'Ar. Ujwal Prabhu N, Nord Studio' },
    { label: 'Manufacturing & execution', value: 'Willow Interiors' },
    { label: 'Client', value: 'Mr. Ashwin' },
    { label: 'Photography', value: 'Agith & Ravindran' },
  ],
  sections: [
    {
      heading: 'Easy to live with',
      photo: 'tv-unit-with-fluted-wood-panel-and-marble-frame',
      paragraphs: [
        'Saral Mane is home to a young couple and their parents, in a G+2 independent house on a 20×40 plot off Kanakapura Road. Their brief was simple: interiors that are easy to maintain, on a tight budget.',
        'With compact rooms, the palette had to open the house up rather than weigh it down. The designer chose browns and soft pastels with a lighter greyish shade, and natural-looking textures — warm, calm and airy.',
      ],
    },
    {
      heading: 'An acrylic look, without the acrylic price',
      photo: 'sage-green-l-shaped-modular-kitchen-with-ivory',
      paragraphs: [
        'The designer wanted an acrylic kitchen; the budget did not allow it. Glossy laminates were not the answer either — their reflections are never seamless, which is exactly why people choose acrylic.',
        'The solution was a 1.25 mm PVC laminate, pasted the way we paste acrylic, over HDHMR board. The result is a smooth, even surface that gives the kitchen an elegant, acrylic-like finish in a tight space — at a laminate budget.',
      ],
    },
    {
      heading: 'Small choices, big differences',
      photo: 'parents-bedroom-taupe-and-white-sliding-wardrobe',
      paragraphs: [
        'Compare the 3D designs with the finished home and you will see differences — and each one is a lesson in cost. The designs showed floor-to-ceiling sliding shutters; to save cost, they became 7 ft shutters with a loft above. One wardrobe was designed with seamless handles; the family chose regular external handles. Each change saved money, and each visibly changed the look.',
        'The takeaway for anyone planning interiors: share your budget with your designer at the very start. A seamless handle or a full-height shutter can move a wardrobe from standard to premium. If budget and design are discussed together early, the designer can plan around it — a wooden or circular handle instead of a seamless one, for example — so the design and the quotation never pull in different directions.',
      ],
    },
    {
      heading: 'Wall panels with no visible screws',
      photo: 'parents-bedroom-stone-finish-wall-panel',
      paragraphs: [
        'Wall panels are the trickiest part of a modular home. Everything else is laminated in the factory, but a panel has to be fixed to the wall — and screws through a finished panel show. Panels are usually finished on site by hand, but families who have watched their furniture being pressed by machine in our factory often do not want manual finishing on site. This family was one of them.',
      ],
      bullets: [
        'Best: design grooves into the panel, place the screws inside the grooves, and paint the grooves black — the screws disappear. Plan the groove spacing so every screw point falls on a groove.',
        'Good: choose a laminate pattern on which a few screw heads simply look acceptable.',
        'Sometimes: cover each screw with a small disc cut from matching edge-band tape — seamless when the edge band and laminate are the same shade.',
        'Avoid: decorative buttons over screws — they stick out and only work if the screws fall in a neat geometric pattern.',
      ],
    },
    {
      heading: 'How it was built',
      paragraphs: ['Every unit was manufactured in Willow’s unit in JP Nagar, Bangalore, and installed by our own team.'],
      bullets: [
        'Laminate finishes throughout, in browns, beiges and light grey.',
        'Kitchen in 1.25 mm PVC laminate pressed onto HDHMR for an acrylic-like finish.',
        'Wardrobes with lofts above 7 ft shutters to keep the budget in check.',
        'Wall panels fixed through black-painted grooves, with no visible screws.',
        'Ebco, Hettich and Häfele hardware; modules built to Willow’s Ennezz Interior Standards (EIS) for a longer life.',
      ],
    },
  ],
  quotes: [
    { text: 'You can never go wrong with browns and beiges.', by: 'Ar. Ujwal Prabhu N, Nord Studio' },
    { text: 'Easy! Just easy. Light and simple.', by: 'Mr. Ashwin' },
  ],
};

// Keyword targets: 3 BHK interiors Banashankari / Hosakerehalli · blue kitchen in an open kitchen ·
// breakfast counter with foyer seating · metal partition · shoe rack with seating · pooja in kitchen ·
// push-to-open loft · fluted glass wardrobe · long wall wardrobe design · study bedroom with storage.
export const BLUE_ON_MARS: ProjectStory = {
  slug: 'blue-on-mars-apartment-banashankari',
  related: [
    { label: "Statement TV consoles on a simple panelled wall", href: '/ideas/tv-unit-designs#statement-tv-console' },
    { label: "Crockery unit designs with lit glass shelves", href: '/ideas/crockery-unit-designs' },
    { label: "Wardrobe ideas, including handle-less push-to-open lofts", href: '/ideas/wardrobe-designs' },
  ],
  name: 'Blue on Mars',
  location: 'The Mars Planet, Hosakerehalli, Banashankari, Bangalore',
  propertyType: 'Apartment · 3 BHK · 1,700 sq ft',
  year: 'Completed',
  tagline:
    'One non-negotiable — a blue kitchen — in an open, long-walled 3 BHK. How a storage-first home in Banashankari was balanced into something calm and premium.',
  metaTitle: "Blue on Mars: 3 BHK Interiors, Banashankari | Willow",
  metaDescription:
    "A 1,700 sq ft 3 BHK in Banashankari: a blue open kitchen with wood and stone textures, a foyer with metal partition and push-to-open wardrobe lofts.",
  cardDescription:
    'A blue open kitchen in a long-walled 3 BHK — balanced with wood, textured grey and soft curves. Storage first, still premium.',
  facts: [
    { label: 'Location', value: 'The Mars Planet, Hosakerehalli, Banashankari, Bangalore' },
    { label: 'Type', value: 'Apartment · 3 BHK · 1,700 sq ft' },
    { label: 'Interior design', value: 'Harshita Seervi, Willow Interiors' },
    { label: 'Design, manufacturing & execution', value: 'Willow Interiors' },
    { label: 'Clients', value: 'Mr. Siva & Mrs. Sonal' },
    { label: 'Photography', value: 'Agith & Ravindran' },
  ],
  sections: [
    {
      heading: 'Storage first — and one rule',
      paragraphs: [
        'Siva and Sonal, a young couple, put storage and everyday use ahead of looks. They had one rule from day one: the kitchen had to be blue.',
        'They chose this home after seeing the interiors Ennezz Interior Solutions had designed for The Mars Planet’s brochure — a guide to getting the most out of its unusually proportioned rooms — and kept that TV unit design. Willow’s job was to make everything else belong with it.',
      ],
    },
    {
      heading: 'A blue kitchen in an open home',
      photo: 'teal-l-shaped-modular-kitchen-with-lit-glass-crockery-unit',
      paragraphs: [
        'Blue is a deep, heavy colour. It sits well in a closed kitchen — but this one is open to the living and dining areas, and long rather than wide, so a full run of blue would have weighed down the whole home.',
        'The answer was to shorten the blue: a foyer and a breakfast counter with seating were introduced at one end, and wood — rather than white or a second colour — was used to balance it.',
      ],
    },
    {
      heading: 'Foyer, seating, shoe rack and pooja — in one run',
      photo: 'foyer-seating-unit-with-arched-metal-partition',
      paragraphs: [
        'Beside the kitchen, a metal partition frames the foyer and works hard: seating, a breakfast counter, a shoe rack and storage. The pooja unit sits within the kitchen itself.',
        'One small piece of on-site carpentry changed the whole space — a soft curve on the seater at the entrance. It softens the blue kitchen and echoes the curve on the TV unit.',
      ],
    },
    {
      heading: 'Colour with a pattern, not at random',
      photo: 'wooden-tv-wall-panel-with-floating-stone-console',
      paragraphs: [
        'With a dark kitchen, the rest of the home could not swing to a light colour. So the TV wall uses a mid-tone wood panel; the slim console is a textured, stone-finish grey that keeps it airy; and a glass shutter lightens the tall unit. The same grey carries into the foyer’s tall unit, with a warm cream — a complement to the blue — on top of the TV unit and in the foyer.',
        'Good colour schemes follow a pattern: soft palettes, a contrast, a dark anchor balanced by light textures, and always a touch of wood. Getting that rhythm right — on a modest budget — is what makes a home feel premium.',
      ],
    },
    {
      heading: 'A long wardrobe wall that still looks premium',
      photo: 'white-high-gloss-wardrobe-with-lit-glass-shutters-master-bedroom',
      paragraphs: [
        'Every main wall here is long — the kitchen, the TV wall and the master bedroom wardrobe. A row of identical shutters would have looked heavy, so the wall was broken up deliberately, and asymmetrically.',
        'A loft keeps the cost down, but usually makes a room look shorter — which is why budget projects rarely look premium. Here, the lofts have no handles at all (push-to-open, since they are rarely used), so the wardrobe reads as one full-height piece. Fluted-glass profile shutters break the pattern, and wooden external drawers on the first two shutters break it again.',
      ],
    },
    {
      heading: 'A study bedroom that fits everything',
      photo: 'window-study-table-with-patterned-base-and-walnut-bookshelf',
      paragraphs: [
        'The study bedroom had to hold a lot: study storage and two wardrobes, with wooden handles for a warmer, more premium feel within budget. The couple wanted it playful, so the study table uses a textured, patterned laminate.',
      ],
    },
    {
      heading: 'How it was built',
      paragraphs: ['Every unit was manufactured in Willow’s unit in JP Nagar, Bangalore, and installed by our own team.'],
      bullets: [
        'Blue kitchen balanced with wood-finish units and a lit glass-front crockery unit.',
        'Metal partition for the foyer, with seating, breakfast counter, shoe rack and storage.',
        'A curved seater shaped on site — the one piece of carpentry that ties the home together.',
        'Stone-finish grey console and fluted-glass profile shutters for texture without weight.',
        'Push-to-open lofts; Ebco, Hettich and Häfele hardware; modules built to Ennezz Interior Standards (EIS).',
      ],
    },
  ],
  quotes: [
    { text: 'No request is too unusual. There’s always a way to make it work — and make it beautiful.', by: 'Harshita Seervi, interior designer' },
    { text: 'Be friends with your designer.', by: 'Siva & Sonal' },
  ],
};

// Keyword targets: apartment interiors Sarjapur Road · sage green kitchen · colourful home interiors ·
// digital laminate wardrobe / digital print laminate · Dorby Mica Uncommon · matte laminate · wooden rafters TV unit ·
// modular rafters without visible screws · living dining partition · interior cost depends on method.
export const SAGE_AND_SUNSHINE: ProjectStory = {
  slug: 'sage-and-sunshine-apartment-sarjapur',
  related: [
    { label: "Printed laminate wardrobes, like the digital-print centre panels here", href: '/ideas/wardrobe-designs#printed-laminate-wardrobe' },
    { label: "Crockery units with a counter", href: '/ideas/crockery-unit-designs#crockery-unit-with-counter' },
    { label: "A console in front of a living–dining partition", href: '/ideas/console-designs#black-textured-console' },
  ],
  name: 'Sage & Sunshine',
  location: 'Sarjapur Road, Bangalore',
  propertyType: 'Apartment · 1,600 sq ft',
  year: 'Completed',
  tagline:
    'A couple who wanted colour everywhere — and fell in love with digital laminates. How sage green, warm woods and one sunny yellow wardrobe became a home that still feels calm.',
  metaTitle: "Sage & Sunshine: Apartment Interiors, Sarjapur | Willow",
  metaDescription:
    "A 1,600 sq ft Sarjapur apartment: a sage green kitchen, digital-print laminate wardrobes, a yellow wardrobe and wooden rafters — all in matte laminate.",
  cardDescription:
    'Sage green, warm woods and a yellow wardrobe — a colour-loving couple’s Sarjapur apartment, with digital laminates used where they work best.',
  facts: [
    { label: 'Location', value: 'Sarjapur Road, Bangalore' },
    { label: 'Type', value: 'Apartment · 1,600 sq ft' },
    { label: 'Interior design', value: 'Harshita Seervi, Willow Interiors' },
    { label: 'Design, manufacturing & execution', value: 'Willow Interiors' },
    { label: 'Client', value: 'Mr. Shrikant' },
    { label: 'Finish', value: 'Matte laminates throughout' },
  ],
  sections: [
    {
      heading: 'A home in colour',
      photo: 'open-living-dining-with-sage-crockery-unit',
      paragraphs: [
        'Shrikant and his wife wanted a colourful home. They came to Willow after falling in love with Uncommon, the digital laminate catalogue by Dorby Mica — laminates printed with patterns, depth and texture — and they wanted those laminates, and bright colours, everywhere.',
        'Colour is personal, and the client’s choices come first. A designer’s job is to make those choices look their best — and to think long-term, because bright trend colours can feel tired in a couple of years, while earthy, neutral tones last. The home that came out of that conversation is colourful, but balanced.',
      ],
    },
    {
      heading: 'Sage green, with warm woods',
      photo: 'sage-green-l-shaped-modular-kitchen-with-wooden-wall-cabinets',
      paragraphs: [
        'The family settled on a soft pastel sage green for the kitchen and living areas. Around it, different browns — a deeper brown in the living room, lighter woods elsewhere — keep the green warm.',
        'Wood is the quiet hero here. Take the wooden touch out of a room and it becomes a different story — which is why every space in this home carries a little of it. Every surface is in matte laminate; nothing is glossy.',
      ],
    },
    {
      heading: 'Digital laminates — as the accent, not the canvas',
      photo: 'mustard-wardrobe-with-patterned-laminate-centre',
      paragraphs: [
        'Digital laminates have rich patterns, depth and texture. That makes them beautiful as an accent — and overwhelming across a large surface like a full wardrobe. So in the two bedrooms, the Dorby Mica digital print laminate sits in the centre panel of each wardrobe, framed by a solid colour: a sunny yellow in one room, brown in the other.',
      ],
    },
    {
      heading: 'A TV unit with storage, and a partition',
      photo: 'sage-fluted-partition-with-lit-display-shelves',
      paragraphs: [
        'The family wanted a large TV unit with plenty of storage. Its fascia is finished with wooden rafters, and the partition between the TV area and the living room — designed by Harshita — separates the two spaces while letting light through.',
      ],
    },
    {
      heading: 'How to make modular rafters look seamless',
      paragraphs: [
        'Rafters are where modular and carpentry finishes clash. In a modular rafter, two boards have to be screwed together, and an edge band covers only one side — so screws or dark edges can show. This family did not want any visible edges, so the rafters were made a different way:',
      ],
      bullets: [
        'Laminate only half the boards in the factory; leave the other half raw.',
        'Join a laminated board to a raw board, screwing from the raw side — glue alone is never enough to hold two boards.',
        'Paste a strip of laminate over the raw side by hand.',
        'Run the joined piece through the edge banding machine with a 45 mm edge band (2 mm, 1.5 mm or 0.8 mm thick) for a seamless finish — no screws and no dark edges visible.',
      ],
    },
    {
      heading: 'Why the method changes the cost',
      paragraphs: [
        'The seamless method takes noticeably more time and effort than a standard modular rafter — even though the finished product looks almost the same. That is worth knowing before you compare quotations: interior cost depends not only on what is made, but on how it is made. A small difference in the final look can take two or three times the work.',
      ],
    },
    {
      heading: 'How it was built',
      paragraphs: ['Every unit was manufactured in Willow’s unit in JP Nagar, Bangalore, and installed by our own team.'],
      bullets: [
        'Matte laminates throughout, in sage green, browns and one yellow.',
        'Dorby Mica digital print laminates (Uncommon catalogue) used as centre panels on the bedroom wardrobes.',
        'Seamless wooden rafters on the TV unit, made with the half-laminated board method.',
        'Ebco, Hettich and Häfele hardware; modules built to Ennezz Interior Standards (EIS) for a longer life.',
      ],
    },
  ],
  quotes: [
    { text: 'Colour is personal. My job is to make it feel like home for years, not just for a season.', by: 'Harshita Seervi, interior designer' },
    { text: 'We wanted colour everywhere — and somehow it still feels calm.', by: 'Mr. Shrikant' },
  ],
};

// Keyword targets: G+3 independent house interiors Bannerghatta Road · 30x50 house interiors ·
// green kitchen laminate / green kitchen shades · interiors for rental floors · interiors on a budget ·
// branded laminates · floating TV unit safety / floor-mounted furniture · long wall wardrobe with dresser.
export const GREEN_STOREY: ProjectStory = {
  slug: 'the-green-storey-g3-house-bannerghatta',
  related: [
    { label: "Interior design for independent houses", href: '/independent-house-interiors-bangalore' },
    { label: "Grooved panel TV walls", href: '/ideas/tv-unit-designs#classic-grooved-panel-tv-wall' },
    { label: "Wardrobe designs with real costs", href: '/ideas/wardrobe-designs' },
  ],
  name: 'The Green Storey',
  location: 'Bannerghatta Road, Bangalore',
  propertyType: 'Independent house · G+3 · 30×50 plot',
  year: 'Completed',
  tagline:
    'A G+3 independent house on a 30×50 plot — the family’s own floors with a green kitchen, and rental floors kept simple and smart. Built on a tight budget, with lessons every homeowner should hear.',
  metaTitle: "The Green Storey: G+3 House Interiors, Bannerghatta | Willow",
  metaDescription:
    "A G+3 house on Bannerghatta Road: a green modular kitchen, long-wall wardrobe with dresser, fluted TV wall and practical rental floors on a tight budget.",
  cardDescription:
    'A G+3 house in Bannerghatta with rental floors — a green kitchen for the family, smart sliding wardrobes for the tenants, all on a tight budget.',
  facts: [
    { label: 'Location', value: 'Bannerghatta Road, Bangalore' },
    { label: 'Type', value: 'Independent house · G+3 · 30×50 plot (1,500 sq ft)' },
    { label: 'Floors', value: 'Family home + rental floors' },
    { label: 'Interior design', value: 'Meghana Prabhudeva, Willow Interiors' },
    { label: 'Design, manufacturing & execution', value: 'Willow Interiors' },
    { label: 'Client', value: 'Mr. Naveen' },
  ],
  sections: [
    {
      heading: 'One house, two kinds of home',
      paragraphs: [
        'Naveen’s G+3 house on a 30×50 plot off Bannerghatta Road is both a family home and an investment: the family lives on their own floors, and the others are rented out. Each needed its own approach — warmth and character for the family, durable and timeless for tenants — all on a tight budget.',
      ],
    },
    {
      heading: 'Choosing a green kitchen',
      photo: 'green-l-shaped-modular-kitchen-with-wooden-lofts',
      paragraphs: [
        'Green kitchens are having a moment — sage, ocean green, blue-greens. The family knew they wanted green, and tried around six shades of green and blue before settling — paired with warm wooden lofts.',
        'A tip if you are choosing one: the shade is only half the decision. The same green looks very different in a matte laminate, a textured or fluted laminate, a gloss, or a PU finish — a colour that is perfect in PU can look flat in laminate, and the other way round. Choose the shade and the finish together, with your designer, from real samples.',
      ],
    },
    {
      heading: 'A long wardrobe wall, with a dressing table',
      photo: 'beige-wardrobe-with-lit-glass-shutters-and-dresser-master-bedroom',
      paragraphs: [
        'In the master bedroom, the wardrobe runs along a very long wall. Two profile glass shutters break up its length, a dressing table is built in, and long handles give it a more premium look on a budget.',
      ],
    },
    {
      heading: 'A kids’ room that works',
      photo: 'kids-bedroom-with-teal-study-unit',
      paragraphs: [
        'The study table and bed sit side by side, placed so the chair has plenty of room to move back and forth. Knobs were used throughout, as the family preferred.',
      ],
    },
    {
      heading: 'A TV wall that feels taller',
      photo: 'fluted-marble-tv-wall-with-lit-glass-display-unit',
      paragraphs: [
        'Fluted laminate on the TV wall draws the eye upward and makes the room feel taller, finished with a wooden rafter detail at the end — a design the family had in mind, which we built for them.',
      ],
    },
    {
      heading: 'Rental floors: simple, durable, a little premium',
      photo: 'white-sliding-door-wardrobe-with-lofts',
      paragraphs: [
        'For the rental floors, the family chose plain colours — the right call for homes that many different tenants will live in. A touch of brown on the sliding wardrobes lifts them, and the rental kitchen is a dark blue with white lofts.',
      ],
    },
    {
      heading: 'Floor-standing, not floating — when there are kids',
      photo: 'wooden-tv-panel-with-white-base-cabinets',
      paragraphs: [
        'Wherever children live, we recommend furniture that stands on the floor rather than floating on the wall. However well it is made, a unit held up only by screws carries a risk of coming away. If a floating TV console is a must, mount it on a metal frame with L-angles for rigidity and load-bearing strength — it costs more, but the risk of it falling is far lower.',
      ],
    },
    {
      heading: 'What this home teaches about budgets',
      paragraphs: [
        'To fit the budget, the crockery unit here became much smaller than designed. It is the most common mistake we see: squeezing every piece of furniture into the budget by shrinking it or simplifying it.',
      ],
      bullets: [
        'Don’t shrink or compromise a piece to make it fit the budget — skip it for now instead. Use simple ready-made storage for a year or two, and build the real piece later.',
        'Get the full design done upfront, by a good designer. The execution can follow in phases, by any company.',
        'Use laminates from a known, widely available brand. Years later you can still find the same colour code, so a new piece will match what you already have.',
        'In your own home, think 30 years ahead — this is where your children will grow up. Interiors are what turn bare walls into a home.',
      ],
    },
    {
      heading: 'How it was built',
      paragraphs: ['Every unit was manufactured in Willow’s unit in JP Nagar, Bangalore, and installed by our own team.'],
      bullets: [
        'Green modular kitchen with wooden lofts for the family; dark blue kitchen with white lofts for the rental floor.',
        'Fluted laminate TV wall with a wooden rafter detail.',
        'Sliding wardrobes in plain colours with a brown accent for the rental floors.',
        'Ebco, Hettich and Häfele hardware; modules built to Ennezz Interior Standards (EIS) for a longer life.',
      ],
    },
  ],
  quotes: [
    { text: 'The best homes are built on trust — when a family lets the designer do what they do best.', by: 'Meghana Prabhudeva, interior designer' },
    { text: 'We wanted green, we got green — and a home that feels just right.', by: 'Mr. Naveen' },
  ],
};

export const PROJECT_STORIES: Record<string, ProjectStory> = {
  [GREEN_STOREY.slug]: GREEN_STOREY,
  [SAGE_AND_SUNSHINE.slug]: SAGE_AND_SUNSHINE,
  [BLUE_ON_MARS.slug]: BLUE_ON_MARS,
  [SARAL_MANE.slug]: SARAL_MANE,
  [GUBBI_GUDU.slug]: GUBBI_GUDU,
  [RASHA.slug]: RASHA,
};
